import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Users, Flame } from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';

// Conversation list for the burn-after-reading party DMs. The endpoint
// only returns pairs that include YOU — and no message text at all, so
// previews can't leak onto a glanced-at screen.
const POLL_MS = 10000;

const DMList = ({ session, onOpenConversation }) => {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);

  const fetchConversations = useCallback(async () => {
    if (!session?.id) return;
    try {
      const { data } = await api.get(`/api/sessions/${session.id}/dms`);
      setConversations(data.conversations || []);
    } catch {
      /* transient — next poll retries */
    }
  }, [session?.id]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (!socket) return undefined;
    const onPing = (data) => {
      if (data?.to === user?.uid) fetchConversations();
    };
    socket.on('dmPing', onPing);
    return () => socket.off('dmPing', onPing);
  }, [socket, user?.uid, fetchConversations]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (conversations.length === 0) {
    return (
      <div className="card h-64 sm:h-80 lg:h-96 max-h-[60vh] flex flex-col">
        <div className="flex items-center mb-4">
          <MessageCircle className="w-5 h-5 mr-2" />
          <h2 className="text-xl font-semibold">Direct Messages</h2>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <Flame className="w-12 h-12 text-orange-400/50 mb-4" />
          <p className="text-gray-400 mb-2">No conversations yet</p>
          <div className="flex items-center text-gray-400 text-sm">
            <Users className="w-4 h-4 mr-1" />
            <span>Start one from the People tab</span>
          </div>
          <p className="text-gray-500 text-xs mt-3 text-center px-6">
            DMs are private to the two of you and vanish about an hour after
            they're read.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-64 sm:h-80 lg:h-96 max-h-[60vh] flex flex-col">
      <div className="flex items-center mb-4">
        <MessageCircle className="w-5 h-5 mr-2" />
        <h2 className="text-xl font-semibold">Direct Messages</h2>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2">
        {conversations.map((convo, index) => (
          <motion.button
            key={convo.withUid}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() =>
              onOpenConversation({ uid: convo.withUid, displayName: convo.withName })
            }
            className="w-full bg-gray-700 hover:bg-gray-600 rounded-lg p-3 transition-colors text-left flex items-center space-x-3"
          >
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {convo.withName?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
              {convo.unread > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {convo.unread > 9 ? '9+' : convo.unread}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className={`font-semibold truncate min-w-0 ${convo.unread > 0 ? 'text-white' : 'text-gray-200'}`}>
                  {convo.withName}
                </h3>
                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                  {formatTime(convo.lastAt)}
                </span>
              </div>
              <p className={`text-sm truncate mt-0.5 ${convo.unread > 0 ? 'text-gray-300 font-medium' : 'text-gray-400'}`}>
                {convo.unread > 0
                  ? `${convo.unread} new ${convo.unread === 1 ? 'message' : 'messages'}`
                  : convo.lastFromMe
                    ? 'You replied'
                    : 'Tap to view'}
              </p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default DMList;
